/**
 * Detect if computer has been sleeping
 * - a.k.a. has time has skipped more than 60 seconds?
 */

/** A time skip larger than this means the machine slept */
export const WAKEUP_THRESHOLD_MS = 60000

export interface WakeUpDetectorOptions {
	/**
	 * Do not report a wakeup while the page is hidden.
	 *
	 * A browser throttles timers in a tab hidden for more than a few minutes to about one tick a
	 * minute, which is indistinguishable from the machine having slept. Set this in a browser; leave
	 * it off in Electron, which runs with backgroundThrottling disabled.
	 */
	ignoreWhileHidden?: boolean
}

class WakeUpDetector {
	private lastTime: number

	private readonly timer: ReturnType<typeof setInterval>

	private readonly callback: () => void

	private readonly ignoreWhileHidden: boolean

	private readonly visibilityListener?: () => void

	/**
	 * Initialize wakeup detector
	 * @param callback Callback to invoke when wake has been detected
	 * @param options Detector options
	 */
	constructor(callback: () => void, options: WakeUpDetectorOptions = {}) {
		this.lastTime = Date.now()
		this.timer = setInterval(this.check.bind(this), 1000)
		this.callback = callback
		this.ignoreWhileHidden = options.ignoreWhileHidden ?? false

		if (this.ignoreWhileHidden && typeof document !== 'undefined') {
			this.visibilityListener = () => {
				// Time that built up while hidden is throttling, not sleep - start counting from now
				if (document.visibilityState === 'visible') { this.lastTime = Date.now() }
			}
			document.addEventListener('visibilitychange', this.visibilityListener)
		}
	}

	/** True if the page is currently hidden */
	private isHidden() {
		return typeof document !== 'undefined' && document.visibilityState === 'hidden'
	}

	/**
	 * Check every seconds to see if time has skipped
	 */
	private check() {
		const current = Date.now()
		const skipped = this.lastTime + WAKEUP_THRESHOLD_MS < current

		this.lastTime = current

		if (!skipped) { return }
		if (this.ignoreWhileHidden && this.isHidden()) { return } // throttled background tab, not a sleeping machine

		this.callback()
	}

	/**
	 * Stop wakeup detector
	 */
	stop() {
		if (this.timer) {
			clearInterval(this.timer)
		}
		if (this.visibilityListener && typeof document !== 'undefined') {
			document.removeEventListener('visibilitychange', this.visibilityListener)
		}
	}

	/**
	 * Create wakeup detector
	 * @param callback Callback to invoke when wake has been detected
	 * @param options Detector options
	 * @returns The detector
	 */
	static create(callback: () => void, options?: WakeUpDetectorOptions) {
		return new WakeUpDetector(callback, options)
	}
}

export default WakeUpDetector
